// src/config/gameProfiles/kcvProfile.ts
// -------------------------------------------------------
// KCV Exam Trainer — refactored into the GameProfile interface.
// Zero behavior change from the original KCV_PROFILE + boardGatekeeper + buildControlPrompt.
// -------------------------------------------------------

import type { GameProfile, GameState, GameEvent } from './GameProfile';

const GATE_SENTENCE = 'Wil je door naar de volgende vraag, of heb je nog vragen?';

const READY_TOKENS = ['ja', 'klaar', 'ready', 'ok', 'oke', 'oké', 'volgende', 'start', 'go'] as const;

const TEACH_TRIGGERS = [
  'ik weet het niet',
  'geen idee',
  'ik snap het niet',
  'leg uit',
  'kun je uitleggen',
  'teach me',
  'i dunno',
  'help',
] as const;

const NEXT_TOKENS = ['volgende', 'next', 'ga door', 'doorgaan', 'ok', 'oke', 'oké', 'ja'] as const;

// ── Helpers ──────────────────────────────────────────────

function isNextConfirm(text: string): boolean {
  const t = (text || '').trim().toLowerCase();
  return (NEXT_TOKENS as readonly string[]).includes(t);
}

function isTeachRequest(text: string): boolean {
  const t = (text || '').trim().toLowerCase();
  return (TEACH_TRIGGERS as readonly string[]).some(trigger => t.includes(trigger));
}

// ── Profile ──────────────────────────────────────────────

export const kcvProfile: GameProfile = {
  id: 'kcv',
  displayName: 'KCV Toets Trainer',

  // ── Phases ─────────────────────────────────────────

  phases: ['INTRO', 'READY_WAIT', 'ASK', 'FEEDBACK', 'TEACH', 'CHECK'] as const,
  initialPhase: 'INTRO',

  transition(state: GameState, event: GameEvent): string | null {
    const { phase } = state;
    const { type } = event;

    switch (phase) {
      case 'INTRO':
      case 'READY_WAIT':
        if (type === 'ready') return 'ASK';
        return 'READY_WAIT';

      case 'ASK':
        if (type === 'teach_requested') return 'TEACH';
        if (type === 'user_response') return 'FEEDBACK';
        return null;

      case 'FEEDBACK':
        if (type === 'next_confirmed') return 'ASK';
        if (type === 'teach_requested') return 'TEACH';
        return null; // stay: student asking follow-ups

      case 'TEACH':
        if (type === 'next_confirmed') return 'ASK';
        return null;

      case 'CHECK':
        if (type === 'user_response') return 'FEEDBACK';
        if (type === 'teach_requested') return 'TEACH';
        return null;

      default:
        return null;
    }
  },

  // ── Board ──────────────────────────────────────────

  boardSource: 'inferred',

  layoutByPhase: {
    INTRO: 'intro',
    READY_WAIT: 'intro',
    ASK: 'question',
    CHECK: 'question',
    FEEDBACK: 'explanation',
    TEACH: 'explanation',
  },

  blockTypes: ['hero', 'chiprow', 'fill', 'grid', 'callout', 'checklist'] as const,

  // ── Prompt injection ───────────────────────────────

  buildControlPrompt(state: GameState, userText: string): string {
    const isGated = state.custom.awaitingNextConfirm === true;

    if (isGated) {
      const allowNext = isNextConfirm(userText);
      if (allowNext) {
        return `TEACHER CADENCE:\nYou may now ask EXACTLY ONE next exam question.\nKeep it short and exam-style. Do NOT add multiple questions.`;
      }
      return `TEACHER CADENCE (GATED):\nYou must NOT ask a new exam question yet.\nThe student is in the 'questions or clarify' step.\nAnswer their follow-up question briefly (max 6 lines),\nthen end with exactly: "${GATE_SENTENCE}"`;
    }

    return `TEACHER CADENCE RULE (VERY IMPORTANT):\n- You quiz in exam style: EXACTLY ONE question per turn.\n- After the student answers a question, you must do ONE teacher turn:\n  1) Say what is correct (1-2 short lines)\n  2) Say what is missing (1-2 short lines)\n  3) Give the correct answer (short, structured, max 8 lines)\n  4) End with exactly: "${GATE_SENTENCE}"\n- Do NOT ask the next exam question in the same turn as the explanation.\n- Only ask the next exam question AFTER the student indicates they want to continue (e.g., "volgende").`;
  },

  buildBoardInstruction(_state: GameState): string {
    // KCV uses Pipeline A (digiboard.php), so no board instruction needed in the system prompt.
    return '';
  },

  // ── Ready gate ─────────────────────────────────────

  readyGateEnabled: true,
  readyAcceptTokens: READY_TOKENS,
  readyPrompt: "Welkom bij KCV-toets trainer Bacchanten & Elektra. Zeg 'ja' als je klaar bent om te starten.",

  // ── Detection ──────────────────────────────────────

  detectKeywords: ['kcv', 'kernconcepten', 'examen', 'toets trainer'] as const,

  // ── Gatekeeper ─────────────────────────────────────

  noAnswerPhases: ['ASK', 'CHECK'] as const,
  allowedBlockTypesInNoAnswerPhases: ['hero', 'chiprow', 'fill', 'callout', 'bullets'] as const,
  forceQuestionMeta: true,

  // ── Event extraction ───────────────────────────────

  classifyUserInput(userText: string, state: GameState): GameEvent {
    const t = (userText || '').trim().toLowerCase();

    // In intro/ready-wait, check for readiness
    if (state.phase === 'INTRO' || state.phase === 'READY_WAIT') {
      if ((READY_TOKENS as readonly string[]).includes(t)) {
        return { type: 'ready' };
      }
      return { type: 'not_ready' };
    }

    // Check for teach triggers
    if (isTeachRequest(userText)) {
      return { type: 'teach_requested' };
    }

    // In gated state, check for next confirmation
    if (state.custom.awaitingNextConfirm && isNextConfirm(userText)) {
      return { type: 'next_confirmed' };
    }

    // Default: it's a user response (answer attempt, follow-up, etc.)
    return { type: 'user_response', payload: { text: userText } };
  },

  classifyAssistantResponse(
    reply: string,
    _script: Record<string, unknown> | null,
    state: GameState
  ): GameEvent {
    // Detect gate sentence at end of reply
    if (reply.trim().endsWith(GATE_SENTENCE)) {
      // Mark in custom state that we're awaiting confirmation
      state.custom.awaitingNextConfirm = true;
      return { type: 'gate_reached' };
    }

    state.custom.awaitingNextConfirm = false;
    return { type: 'assistant_spoke' };
  },
};
